/**
 * What a route declares about itself.
 *
 * Its own file so every per-domain manifest under `routes/` can import the type without importing
 * the aggregator that imports them back. `routes.ts` re-exports it, so the existing import surface
 * — `import type { RouteManifestEntry } from './routes.js'` — is unchanged.
 *
 * Both imports below came with the interface when it was extracted from `routes.ts` and were left
 * behind. `vite build` never noticed: it transpiles per file and erases types without checking
 * them, so the app built and ran while `tsc --noEmit` — which is what CI's S3 runs — did not.
 */

import type { ComponentType } from 'react';
import type { MessageKey } from '@indovation/i18n';

/**
 * The three groups the sidebar is divided into — M-25, change order 06.
 *
 * They answer three different questions and that is the whole of the reasoning: **the work**
 * (clients, requisitions, people, the board), **how it is going** (everything derived from what
 * the work produced), and **running the agency** (who is here, what is configured). A reader
 * looking for a screen knows which of the three they are in before they read a single label.
 *
 * Two properties are load-bearing and both were failures of the five-section shape this replaces
 * (CO2 §2.1, restructured 16 Aug 2026):
 *
 * - **No section holds one item.** A one-item section is an item with a heading over it, which
 *   costs a line of vertical space and buys nothing.
 * - **Later packs need no new section.** Outreach's inbox and Engage both land in `recruiting`; a
 *   candidate-portal admin screen lands in `administration`. The *structure* of the nav stops
 *   changing after this slice, which is what makes it manageable per release — a new capability
 *   adds a row, never a heading.
 *
 * Home is deliberately outside all three. It is the default route and the way back, not a member
 * of a category, and giving it a heading of its own would break the first property immediately.
 *
 * `gates/route-manifest-lint.mjs` reads this array by name. Renaming the constant without moving
 * the gate makes the gate stop checking rather than start failing, which is why it is exported
 * from the type file rather than declared inline in each manifest.
 */
export const NAV_SECTIONS = ['recruiting', 'insights', 'administration'] as const;

export type NavSection = (typeof NAV_SECTIONS)[number];

export interface RouteManifestEntry {
  readonly id: string;
  readonly path: string;
  /** A module key from a module's ownership manifest, or null with an exemption below. */
  readonly entitlement: string | null;
  /** Required when `entitlement` is null. Prose, not a flag. */
  readonly entitlementExemption?: string;
  /** Whether a signed-in principal is required. */
  readonly authenticated: boolean;
  /**
   * Where this appears in the sidebar, if it does. Navigation is derived from the manifest rather
   * than written separately, so a route gated on an entitlement gets a nav item gated on the same
   * one — a second list would be the place the two quietly disagree.
   */
  readonly nav?: {
    readonly labelKey: MessageKey;
    /**
     * Which of the three groups it sits in, or `null` for the one route that sits above them all.
     *
     * Explicitly nullable rather than optional: "this route is deliberately sectionless" and
     * "somebody forgot" are different states, and an optional field cannot tell them apart. The
     * gate refuses an absent `section`; it accepts `null` exactly where the manifest says so.
     */
    readonly section: NavSection | null;
    /** Position **within** the section. Meaningless across sections, which is why it repeats. */
    readonly order: number;
  };
  /**
   * The permission a principal needs to reach this screen — arch §4.3 layer 3, as distinct from
   * the entitlement above, which is layer 2.
   *
   * They answer different questions and a screen can need both: `clients` says the *tenant* bought
   * the module, `{ action: 'write', resource: 'client' }` says *this person* may use that part of
   * it. Collapsing them would mean a tenant losing a module and a recruiter losing a permission
   * being the same failure with the same message, which is wrong in both directions.
   *
   * Checked against the same matrix the API decides with (`@indovation/policy-client`), and the
   * server re-checks regardless.
   */
  readonly requires?: { readonly action: string; readonly resource: string };
  /**
   * The URL-addressable state of this screen, if it has any.
   *
   * A screen holding its filters in component state cannot be linked to, shared, or returned to —
   * which is the debt the book-of-work slice recorded ("moving those filters into search params
   * fixes it, and gives the screen a shareable *here is what I am looking at*"). Declared here
   * rather than in `app.tsx` for the same reason everything else is: one routing table, and the
   * gate reads it.
   *
   * Optional, and most screens have none. Adding it to a screen is what makes saved views (§6.1)
   * possible later — a saved view is a named set of search params, and there is nothing to name
   * until the params exist.
   */
  readonly validateSearch?: (search: Record<string, unknown>) => Record<string, unknown>;
  readonly load: () => Promise<{ default: ComponentType }>;
}
